/**
 * AfterCare — Template Response Engine
 *
 * Provides structured, data-driven responses for common medication questions
 * BEFORE the LLM finishes downloading (~800MB). This is the CRITICAL path:
 * patients see these responses first, potentially for minutes while the model
 * loads on slow connections.
 *
 * Design principles:
 *   - TTS-optimized: simple sentences, no markdown, no bullet points
 *   - 5th grade reading level (Flesch-Kincaid ~80+)
 *   - Personalized with patient name when available
 *   - Returns null for intents that genuinely need LLM synthesis
 *   - Zero network calls — all data from localStorage
 */

import {
  getPatientName,
  getPatientMedications,
  getDrugFromCache,
  getDrugInteractions,
  searchDrugsFromCache,
} from './db';
import type { PatientMedRecord } from './db';
import {
  getActiveAllergies,
  getAbnormalLabs,
  getActiveDiagnoses,
  getUpcomingFollowUps,
  getEmrRecord,
  getLatestVitals,
} from './emr-store';
import { t } from '../i18n';
import type { IntentType } from '../types/index';

// ---------------------------------------------------------------------------
// Known medication-to-diagnosis mappings
// ---------------------------------------------------------------------------

/**
 * Maps generic drug names (lowercased) to the conditions they treat.
 * Used by the `why_taking` intent to connect a medication to its purpose
 * without requiring LLM inference.
 */
const DRUG_DIAGNOSIS_MAP: Record<string, string> = {
  metformin: 'Type 2 Diabetes',
  lisinopril: 'High Blood Pressure',
  atorvastatin: 'High Cholesterol',
  omeprazole: 'Acid Reflux',
  amlodipine: 'High Blood Pressure',
  losartan: 'High Blood Pressure',
  simvastatin: 'High Cholesterol',
  rosuvastatin: 'High Cholesterol',
  pravastatin: 'High Cholesterol',
  glipizide: 'Type 2 Diabetes',
  glyburide: 'Type 2 Diabetes',
  sitagliptin: 'Type 2 Diabetes',
  pioglitazone: 'Type 2 Diabetes',
  pantoprazole: 'Acid Reflux',
  esomeprazole: 'Acid Reflux',
  lansoprazole: 'Acid Reflux',
  hydrochlorothiazide: 'High Blood Pressure',
  furosemide: 'Fluid Retention',
  warfarin: 'Blood Clot Prevention',
  apixaban: 'Blood Clot Prevention',
  clopidogrel: 'Blood Clot Prevention',
  aspirin: 'Heart Protection',
  levothyroxine: 'Thyroid Condition',
  prednisone: 'Inflammation',
  gabapentin: 'Nerve Pain',
  sertraline: 'Depression or Anxiety',
  fluoxetine: 'Depression or Anxiety',
  alprazolam: 'Anxiety',
  acetaminophen: 'Pain Relief',
  ibuprofen: 'Pain and Inflammation',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract a drug name from the question text by checking against the
 * patient's active medication list. Returns the first matching
 * PatientMedRecord or null.
 */
function extractDrugFromQuestion(question: string): PatientMedRecord | null {
  const lower = question.toLowerCase();
  const meds = getPatientMedications();

  for (const med of meds) {
    // Match against the full drug name (e.g. "Metformin 500mg")
    if (lower.includes(med.drug_name.toLowerCase())) {
      return med;
    }
    // Match against just the base name (e.g. "metformin")
    const baseName = med.drug_name.split(/\s+/)[0].toLowerCase();
    if (lower.includes(baseName)) {
      return med;
    }
  }

  return null;
}

/**
 * Look up the drug cache record for a PatientMedRecord.
 * Tries drug_rxcui first, then falls back to name-based search.
 */
function lookupDrugData(med: PatientMedRecord): Record<string, unknown> | null {
  // Try by RxCUI first (exact match)
  let drug = getDrugFromCache(med.drug_rxcui);
  if (drug) return drug;

  // Fall back to name search
  const baseName = med.drug_name.split(/\s+/)[0];
  const results = searchDrugsFromCache(baseName);
  if (results.length > 0) return results[0].meta;

  return null;
}

/**
 * Format a 24h time string like "08:00" into spoken English like "8 AM".
 */
function formatTimeSpoken(time24: string): string {
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  if (m === 0) {
    return `${hour12} ${period}`;
  }
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

/**
 * Build a greeting prefix using the patient's first name.
 * Returns empty string if no name is set.
 */
function greeting(): string {
  const name = getPatientName();
  return name ? `${name}, ` : '';
}

/**
 * Join an array of strings in a spoken-friendly way.
 * ["A", "B", "C"] -> "A, B, and C"
 */
function joinSpoken(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return items.slice(0, -1).join(', ') + ', and ' + items[items.length - 1];
}

// ---------------------------------------------------------------------------
// Intent handlers
// ---------------------------------------------------------------------------

function handleNextDose(): string {
  const meds = getPatientMedications();
  if (meds.length === 0) {
    return t('voice.noMedsForDose');
  }

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  // Build a sorted list of upcoming doses
  const upcoming: { name: string; time: string; minutesAway: number }[] = [];

  for (const med of meds) {
    for (const timeStr of med.times) {
      const [h, m] = timeStr.split(':').map(Number);
      const medMinutes = h * 60 + m;
      let diff = medMinutes - nowMinutes;
      // If the dose was more than 30 minutes ago, wrap to tomorrow
      if (diff < -30) diff += 1440;
      if (diff >= -30) {
        upcoming.push({
          name: med.drug_name,
          time: formatTimeSpoken(timeStr),
          minutesAway: diff,
        });
      }
    }
  }

  upcoming.sort((a, b) => a.minutesAway - b.minutesAway);

  if (upcoming.length === 0) {
    return `${greeting()}you have taken all your medications for today. Great job!`;
  }

  const next = upcoming[0];
  let response = `${greeting()}your next dose is ${next.name}. You should take it at ${next.time}.`;

  // If there is another dose coming up, mention it too
  if (upcoming.length > 1) {
    const after = upcoming[1];
    response += ` After that, your next one is ${after.name} at ${after.time}.`;
  }

  return response;
}

function handleMyMedications(): string {
  const meds = getPatientMedications();
  if (meds.length === 0) {
    return t('voice.noMedsForDose');
  }

  const medParts: string[] = [];
  for (const med of meds) {
    const times = med.times.map(formatTimeSpoken);
    const timeStr = joinSpoken(times);
    medParts.push(`${med.drug_name}, taken at ${timeStr}`);
  }

  const g = greeting();
  const count = meds.length;
  return `${g}you are taking ${count} medication${count > 1 ? 's' : ''}. ${joinSpoken(medParts)}. If you have any questions about any of them, just ask.`;
}

function handleLogTaken(question: string): string {
  const med = extractDrugFromQuestion(question);
  const name = med ? med.drug_name : 'your medication';
  return t('voice.loggedTaken', { drug: name });
}

function handleLogSkipped(question: string): string {
  const med = extractDrugFromQuestion(question);
  const name = med ? med.drug_name : 'your medication';
  return t('voice.loggedSkipped', { drug: name });
}

function handleHelp(): string {
  return t('voice.help');
}

function handleShowEmr(): string {
  const diagnoses = getActiveDiagnoses();
  if (diagnoses.length === 0) {
    return `${greeting()}I do not have any diagnoses on file for you right now.`;
  }

  const names = diagnoses.map((d) => d.description);
  return `${greeting()}your active diagnoses are ${joinSpoken(names)}. Would you like to know more about any of them?`;
}

function handleEmrDiagnosis(): string {
  return handleShowEmr();
}

function handleEmrAllergies(): string {
  const allergies = getActiveAllergies();
  if (allergies.length === 0) {
    return `${greeting()}I do not have any allergies on file for you. If you know of any, please tell your doctor.`;
  }

  const parts = allergies.map((a) => {
    const severity = a.severity ? ` which is ${a.severity}` : '';
    return `${a.allergen}${severity}`;
  });

  return `${greeting()}your allergies on file are ${joinSpoken(parts)}. Always remind any new doctor or pharmacist about these before starting a new medication.`;
}

function handleMyMedicationsList(): string {
  const meds = getPatientMedications();
  if (meds.length === 0) {
    return `${greeting()}I do not have any medications on file for you right now.`;
  }

  const active = meds.filter((m) => m.active);
  const parts = active.map((m) => {
    const times = m.times.map(formatTimeSpoken).join(' and ');
    return `${m.drug_name}, which you take at ${times}`;
  });

  return `${greeting()}you are taking ${active.length} medications. ${joinSpoken(parts)}. Would you like to know more about any of them?`;
}

function handleLabQuestion(): string {
  const abnormal = getAbnormalLabs();
  if (abnormal.length === 0) {
    return `${greeting()}all of your recent lab results are in the normal range. That is good news.`;
  }

  const parts: string[] = [];
  for (const lab of abnormal.slice(0, 5)) {
    const interp = lab.interpretation === 'high' || lab.interpretation === 'critical-high'
      ? 'a little high'
      : 'a little low';
    parts.push(
      `Your ${lab.testName} is ${interp} at ${lab.value} ${lab.unit}. The normal range is ${lab.referenceRange}.`
    );
  }

  return `${greeting()}here are your lab results that need attention. ${parts.join(' ')} Your doctor is watching these numbers. If you have questions, ask at your next visit.`;
}

function handleEmrFollowUp(): string {
  const followUps = getUpcomingFollowUps();
  if (followUps.length === 0) {
    return `${greeting()}you do not have any upcoming appointments on file. Please check with your doctor's office.`;
  }

  const parts: string[] = [];
  for (const fu of followUps.slice(0, 4)) {
    const dateObj = new Date(fu.date + 'T00:00:00');
    const month = dateObj.toLocaleString('en-US', { month: 'long' });
    const day = dateObj.getDate();
    parts.push(`${fu.specialty} with ${fu.provider} on ${month} ${day} for ${fu.reason}`);
  }

  return `${greeting()}you have ${followUps.length} upcoming appointment${followUps.length > 1 ? 's' : ''}. ${joinSpoken(parts)}. If you need to reschedule, call the number on your discharge papers.`;
}

function handleEmrDischargeInfo(): string {
  const emr = getEmrRecord();
  if (!emr) {
    return `${greeting()}I do not have discharge information on file right now.`;
  }

  const ds = emr.dischargeSummary;
  const dischargeDate = new Date(ds.dischargeDate + 'T00:00:00');
  const month = dischargeDate.toLocaleString('en-US', { month: 'long' });
  const day = dischargeDate.getDate();

  let response = `${greeting()}you were discharged from ${ds.hospitalName} on ${month} ${day}. `;
  response += `Your condition at discharge was ${ds.dischargeCondition.toLowerCase()}. `;

  if (ds.dietaryInstructions) {
    response += `For your diet, ${ds.dietaryInstructions.charAt(0).toLowerCase() + ds.dietaryInstructions.slice(1)} `;
  }

  if (ds.warningSignsToWatch.length > 0) {
    const signs = ds.warningSignsToWatch.slice(0, 3);
    response += `Watch out for ${joinSpoken(signs)}. If you notice any of these, call your doctor right away.`;
  }

  return response;
}

function handleSideEffects(question: string): string {
  const med = extractDrugFromQuestion(question);
  const meds = med ? [med] : getPatientMedications();

  if (meds.length === 0) {
    return t('voice.noMedsForDose');
  }

  const parts: string[] = [];
  for (const m of meds.slice(0, 4)) {
    const drug = lookupDrugData(m);
    if (!drug) continue;

    const effects = (drug.side_effects as string[]) ?? [];
    if (effects.length === 0) continue;

    const top = effects.slice(0, 5);
    const baseName = m.drug_name.split(/\s+/)[0];
    parts.push(`For ${baseName}, the most common side effects are ${joinSpoken(top)}.`);
  }

  if (parts.length === 0) {
    return `${greeting()}I do not have side effect information on file for your medications right now. Please ask your pharmacist.`;
  }

  return `${greeting()}${parts.join(' ')} If any side effect is severe or does not go away, contact your doctor.`;
}

function handleMissedDose(question: string): string {
  const med = extractDrugFromQuestion(question);

  if (med) {
    const drug = lookupDrugData(med);
    if (drug && drug.missed_dose) {
      return `${greeting()}if you missed a dose of ${med.drug_name}, here is what to do. ${drug.missed_dose} Do not take two doses at the same time to make up for it.`;
    }
    return `${greeting()}if you missed a dose of ${med.drug_name}, take it as soon as you remember, unless it is almost time for your next dose. Do not double up. If you are not sure, call your pharmacist.`;
  }

  // General guidance
  return `${greeting()}if you missed a dose, the general rule is to take it as soon as you remember. But if it is almost time for your next dose, skip the missed one. Never take two doses at once. If you are not sure, call your pharmacist.`;
}

function handleFoodInteractions(question: string): string {
  const med = extractDrugFromQuestion(question);
  const meds = med ? [med] : getPatientMedications();

  if (meds.length === 0) {
    return t('voice.noMedsForDose');
  }

  const parts: string[] = [];
  for (const m of meds.slice(0, 4)) {
    const drug = lookupDrugData(m);
    if (!drug) continue;

    const interactions = (drug.food_interactions as string[]) ?? [];
    if (interactions.length === 0) continue;

    const baseName = m.drug_name.split(/\s+/)[0];
    parts.push(`For ${baseName}, ${joinSpoken(interactions)}.`);
  }

  if (parts.length === 0) {
    return `${greeting()}I do not have any food interaction warnings on file for your medications. That said, always take your medications as directed. If you have questions, ask your pharmacist.`;
  }

  return `${greeting()}here are some important food interactions to know about. ${parts.join(' ')}`;
}

function handleWhyTaking(question: string): string {
  const med = extractDrugFromQuestion(question);

  if (!med) {
    // No specific drug mentioned — list all with reasons
    const meds = getPatientMedications();
    if (meds.length === 0) return t('voice.noMedsForDose');

    const parts: string[] = [];
    for (const m of meds) {
      const baseName = m.drug_name.split(/\s+/)[0].toLowerCase();
      const reason = DRUG_DIAGNOSIS_MAP[baseName];
      if (reason) {
        parts.push(`${m.drug_name} is for your ${reason}`);
      } else {
        parts.push(`${m.drug_name}`);
      }
    }

    return `${greeting()}here is why you are taking each of your medications. ${joinSpoken(parts)}. Your doctor prescribed these based on your health needs.`;
  }

  // Specific drug mentioned
  const baseName = med.drug_name.split(/\s+/)[0].toLowerCase();
  const reason = DRUG_DIAGNOSIS_MAP[baseName];

  if (reason) {
    // Try to cross-reference with active diagnoses for richer context
    const diagnoses = getActiveDiagnoses();
    const matchingDx = diagnoses.find((d) =>
      d.description.toLowerCase().includes(reason.toLowerCase().split(' ')[0])
    );

    if (matchingDx) {
      return `${greeting()}you are taking ${med.drug_name} to help manage your ${matchingDx.description}. Your doctor prescribed it on ${matchingDx.onsetDate}. ${med.instructions}`;
    }

    return `${greeting()}you are taking ${med.drug_name} to help with your ${reason}. ${med.instructions}`;
  }

  // No known mapping — try the drug description from the cache
  const drug = lookupDrugData(med);
  if (drug && drug.description) {
    const desc = String(drug.description);
    return `${greeting()}you are taking ${med.drug_name} because ${desc.charAt(0).toLowerCase() + desc.slice(1)}`;
  }

  return `${greeting()}you are taking ${med.drug_name} as prescribed by your doctor. If you would like to know more about why, please ask at your next appointment.`;
}

function handleStorage(question: string): string {
  const med = extractDrugFromQuestion(question);

  if (!med) {
    return `${greeting()}most medications should be stored at room temperature, away from heat and moisture. Keep them out of the bathroom and away from children. If you want to know about a specific medication, just say its name.`;
  }

  const drug = lookupDrugData(med);
  if (drug && drug.storage) {
    return `${greeting()}for ${med.drug_name}, ${String(drug.storage).charAt(0).toLowerCase() + String(drug.storage).slice(1)}`;
  }

  return `${greeting()}store ${med.drug_name} at room temperature, away from heat and moisture. Keep it in its original container and out of reach of children.`;
}

function handleGenericVsBrand(question: string): string {
  const med = extractDrugFromQuestion(question);

  if (!med) {
    return `${greeting()}generic and brand name medications have the same active ingredient and work the same way. Generics are usually less expensive. If you want to know about a specific medication, just say its name.`;
  }

  const drug = lookupDrugData(med);
  if (drug) {
    const brands = (drug.brand_names as string[]) ?? [];
    const genericName = String(drug.generic_name || med.drug_name.split(/\s+/)[0]);

    if (brands.length > 0) {
      return `${greeting()}${med.drug_name} contains the generic ingredient ${genericName}. The brand name${brands.length > 1 ? 's are' : ' is'} ${joinSpoken(brands)}. Generic and brand name versions work the same way, but generics usually cost less.`;
    }

    return `${greeting()}${med.drug_name} contains ${genericName}. I do not have brand name information on file, but your pharmacist can tell you about available options.`;
  }

  return `${greeting()}I do not have brand name information for ${med.drug_name} right now. Your pharmacist can help you compare generic and brand name options.`;
}

// ---------------------------------------------------------------------------
// Cross-condition handlers (360-degree EMR view)
// ---------------------------------------------------------------------------

/**
 * Known side effects by drug for cross-referencing symptoms.
 * Maps drug base name → array of {symptom keywords, description}.
 */
const DRUG_SIDE_EFFECT_MAP: Record<string, { keywords: string[]; description: string }[]> = {
  metformin: [
    { keywords: ['stomach', 'nausea', 'diarrhea', 'cramp', 'upset', 'vomit'], description: 'stomach upset, nausea, or diarrhea. This is common when starting Metformin. Taking it with food usually helps' },
    { keywords: ['tingling', 'numb', 'pins'], description: 'tingling or numbness, which can sometimes be related to low vitamin B12 levels from long-term Metformin use' },
  ],
  lisinopril: [
    { keywords: ['dizz', 'lightheaded', 'faint'], description: 'dizziness or lightheadedness, which can happen with blood pressure medications like Lisinopril, especially when you stand up quickly' },
    { keywords: ['cough', 'dry cough'], description: 'a dry cough, which is a known side effect of Lisinopril' },
    { keywords: ['headache'], description: 'headaches, which can sometimes occur with blood pressure medication' },
  ],
  atorvastatin: [
    { keywords: ['muscle', 'ache', 'weakness', 'pain', 'sore'], description: 'muscle pain or weakness. This is an important side effect to report to your doctor right away, as it can rarely indicate a more serious issue with Atorvastatin' },
    { keywords: ['stomach', 'nausea', 'upset'], description: 'stomach discomfort, which can sometimes happen with Atorvastatin' },
  ],
  omeprazole: [
    { keywords: ['headache'], description: 'headaches, which can sometimes happen with Omeprazole' },
    { keywords: ['stomach', 'nausea', 'diarrhea'], description: 'stomach discomfort or diarrhea with Omeprazole' },
  ],
};

/**
 * Conditions that relate to specific symptoms for clinical context.
 */
const CONDITION_SYMPTOM_MAP: Record<string, { keywords: string[]; explanation: string }[]> = {
  'diabetes': [
    { keywords: ['tingling', 'numb', 'pins', 'burning'], explanation: 'tingling and numbness in the feet or hands can be a sign of diabetic neuropathy, which happens when high blood sugar damages nerves over time. Your HbA1c is currently a bit high, so managing your blood sugar is important to prevent this from getting worse' },
    { keywords: ['dizz', 'lightheaded', 'shak', 'sweat'], explanation: 'dizziness and shakiness can be a sign of low blood sugar. If you feel this way, check your blood sugar if you can, eat or drink something with sugar, and sit down until you feel better' },
    { keywords: ['blur', 'vision'], explanation: 'blurred vision can happen when blood sugar is too high or too low. It can also be a sign of diabetic eye changes. You had a retinal exam in September 2025 that was normal, but let your doctor know if this continues' },
    { keywords: ['thirst', 'urinating', 'peeing'], explanation: 'increased thirst and frequent urination can be signs that your blood sugar is running too high' },
  ],
  'hypertension': [
    { keywords: ['dizz', 'lightheaded', 'faint'], explanation: 'dizziness when standing up can happen because your blood pressure medication, Lisinopril, is lowering your blood pressure. Try standing up slowly and sitting on the edge of the bed before getting up. Your blood pressure was 126 over 78 at discharge, which is well controlled' },
    { keywords: ['headache', 'head'], explanation: 'headaches can sometimes be related to blood pressure changes. Your blood pressure was well controlled at discharge, but if headaches are severe or sudden, contact your doctor' },
  ],
};

function handleSymptomConcern(question: string): string {
  const lower = question.toLowerCase();
  const diagnoses = getActiveDiagnoses();
  const meds = getPatientMedications();
  const emr = getEmrRecord();

  const parts: string[] = [];
  let foundMatch = false;

  // Check if this symptom matches any known drug side effects
  for (const med of meds) {
    const baseName = med.drug_name.split(/\s+/)[0].toLowerCase();
    const effects = DRUG_SIDE_EFFECT_MAP[baseName];
    if (!effects) continue;

    for (const effect of effects) {
      if (effect.keywords.some(k => lower.includes(k))) {
        parts.push(`One possibility is that ${med.drug_name} can sometimes cause ${effect.description}.`);
        foundMatch = true;
        break;
      }
    }
  }

  // Check if this symptom connects to any of their conditions
  for (const dx of diagnoses) {
    const dxLower = dx.description.toLowerCase();
    for (const [condition, symptoms] of Object.entries(CONDITION_SYMPTOM_MAP)) {
      if (!dxLower.includes(condition)) continue;
      for (const symptom of symptoms) {
        if (symptom.keywords.some(k => lower.includes(k))) {
          parts.push(`Given your ${dx.description}, ${symptom.explanation}.`);
          foundMatch = true;
          break;
        }
      }
    }
  }

  // Check against discharge warning signs
  if (emr?.dischargeSummary?.warningSignsToWatch) {
    const warningSigns = emr.dischargeSummary.warningSignsToWatch;
    for (const sign of warningSigns) {
      const signLower = sign.toLowerCase();
      // Check if any words from the question match the warning sign
      const questionWords = lower.split(/\s+/).filter(w => w.length > 3);
      const matchesWarning = questionWords.some(w => signLower.includes(w));
      if (matchesWarning) {
        parts.push(`Important: your discharge papers say to watch out for ${sign}. If this is what you are experiencing, please call your doctor right away.`);
        foundMatch = true;
        break;
      }
    }
  }

  if (!foundMatch) {
    return `${greeting()}I understand you are not feeling well. While I cannot diagnose what is causing this, I can tell you that your discharge papers list these warning signs to watch for: ${emr?.dischargeSummary?.warningSignsToWatch?.slice(0, 4).join(', ') || 'none on file'}. If your symptoms are severe or getting worse, please call your doctor at your next appointment or call the number on your discharge papers.`;
  }

  let response = `${greeting()}I am glad you told me about this. `;
  response += parts.join(' ');
  response += ' I am not a doctor, so please mention this to your care team. If it is severe or getting worse, do not wait for your next appointment. Call your doctor right away.';

  return response;
}

function handleEmotionalConcern(question: string): string {
  const lower = question.toLowerCase();
  const diagnoses = getActiveDiagnoses();
  const followUps = getUpcomingFollowUps();
  const emr = getEmrRecord();
  const carePlan = emr?.carePlan;

  let response = `${greeting()}I hear you, and what you are feeling is completely normal. Many people feel this way after being in the hospital. `;

  // Connect to their specific situation
  if (/\b(?:scared|afraid|worried|terrified|frightened)\b/.test(lower)) {
    const conditionNames = diagnoses.map(d => d.description.split(',')[0]).slice(0, 3);
    if (conditionNames.length > 0) {
      response += `Managing ${joinSpoken(conditionNames)} can feel overwhelming, but you are not doing this alone. Your medical team has a plan to help you get better. `;
    }

    // If worried about a specific condition
    if (/\bdiabetes\b/.test(lower)) {
      const labs = getAbnormalLabs();
      const hba1c = labs.find(l => l.testName.toLowerCase().includes('a1c'));
      if (hba1c) {
        response += `Your HbA1c is ${hba1c.value}${hba1c.unit}, which means there is room to improve, but you are not in a danger zone. Small steps like taking your Metformin on time and following your diet plan can make a big difference. `;
      }
    }
  }

  if (/\b(?:overwhelmed|too\s+much|can'?t\s+cope)\b/.test(lower)) {
    const medCount = getPatientMedications().length;
    response += `Taking ${medCount} medications and keeping track of everything can feel like a lot. Try focusing on one thing at a time. I am here to help you remember your schedule and answer any questions. `;
  }

  // Point to upcoming support
  if (followUps.length > 0) {
    const nextAppt = followUps[0];
    const dateObj = new Date(nextAppt.date + 'T00:00:00');
    const month = dateObj.toLocaleString('en-US', { month: 'long' });
    const day = dateObj.getDate();
    response += `Your next appointment is ${nextAppt.specialty} with ${nextAppt.provider} on ${month} ${day}. That would be a good time to talk about how you are feeling. `;
  }

  response += 'If you are feeling really down or need someone to talk to right away, you can call 988, which is the Suicide and Crisis Lifeline. They help with all kinds of emotional distress, not just emergencies.';

  return response;
}

function handleDietQuestion(question: string): string {
  const lower = question.toLowerCase();
  const emr = getEmrRecord();
  const diagnoses = getActiveDiagnoses();
  const dietaryInstructions = emr?.dischargeSummary?.dietaryInstructions;

  let response = `${greeting()}`;

  // Check for specific foods
  const hasDiabetes = diagnoses.some(d => d.description.toLowerCase().includes('diabetes'));
  const hasHypertension = diagnoses.some(d => d.description.toLowerCase().includes('hypertension'));

  if (/\b(?:rice|bread|pasta|carbs?|potato|noodles)\b/.test(lower)) {
    if (hasDiabetes) {
      response += 'with your diabetes, you can still eat rice and bread, but in smaller portions and preferring whole grain versions. White rice and white bread can raise your blood sugar quickly. Try brown rice, whole wheat bread, or other whole grains instead. Pair them with vegetables and protein to slow down the sugar spike. ';
    }
  } else if (/\b(?:sugar|sweets?|candy|cake|cookies?|ice\s*cream|dessert|soda|juice)\b/.test(lower)) {
    if (hasDiabetes) {
      response += 'with your diabetes, you should limit added sugars and sweets. They can cause your blood sugar to spike. If you are craving something sweet, a small piece of fruit is a better choice. Avoid sugary drinks like soda and juice. ';
    }
  } else if (/\b(?:salt|salty|sodium|chips|fries)\b/.test(lower)) {
    if (hasHypertension) {
      response += 'with your high blood pressure, you should limit sodium to 1,500 milligrams per day. Avoid adding salt to food, and watch out for sodium in processed foods, canned soups, and fast food. ';
    }
  } else {
    // General diet guidance
    if (dietaryInstructions) {
      response += `based on your discharge instructions, ${dietaryInstructions.charAt(0).toLowerCase() + dietaryInstructions.slice(1)} `;
    } else {
      response += 'your doctor recommends a balanced diet with plenty of vegetables, lean proteins, and whole grains. ';
    }
  }

  // Add condition-specific context
  if (hasDiabetes && !response.includes('diabetes')) {
    response += 'Since you have diabetes, eating at regular times and not skipping meals is important, especially while taking Metformin. ';
  }

  // Mention the nutritionist if scheduled
  const followUps = getUpcomingFollowUps();
  const nutritionist = followUps.find(f => f.specialty.toLowerCase().includes('nutrition'));
  if (nutritionist) {
    const dateObj = new Date(nutritionist.date + 'T00:00:00');
    const month = dateObj.toLocaleString('en-US', { month: 'long' });
    const day = dateObj.getDate();
    response += `You have a nutrition appointment with ${nutritionist.provider} on ${month} ${day}. They can create a personalized eating plan for you.`;
  }

  return response;
}

function handleOtcSafety(question: string): string {
  const lower = question.toLowerCase();
  const meds = getPatientMedications();
  const allergies = getActiveAllergies();
  const diagnoses = getActiveDiagnoses();

  let otcDrug = '';
  if (/\b(?:ibuprofen|advil|motrin)\b/.test(lower)) otcDrug = 'ibuprofen';
  else if (/\b(?:tylenol|acetaminophen)\b/.test(lower)) otcDrug = 'acetaminophen';
  else if (/\baspirin\b/.test(lower)) otcDrug = 'aspirin';
  else if (/\b(?:naproxen|aleve)\b/.test(lower)) otcDrug = 'naproxen';
  else if (/\b(?:tums|antacid)\b/.test(lower)) otcDrug = 'tums';
  else if (/\b(?:pepto|bismol)\b/.test(lower)) otcDrug = 'pepto-bismol';
  else if (/\b(?:benadryl|diphenhydramine)\b/.test(lower)) otcDrug = 'benadryl';
  else otcDrug = 'that medication';

  let response = `${greeting()}`;

  // Check allergies first
  const allergenNames = allergies.map(a => a.allergen.toLowerCase());

  // NSAID concerns with kidney/diabetes/BP
  const hasKidneyConcern = diagnoses.some(d => d.description.toLowerCase().includes('diabetes') || d.description.toLowerCase().includes('hypertension'));
  const takesLisinopril = meds.some(m => m.drug_name.toLowerCase().includes('lisinopril'));
  const takesMetformin = meds.some(m => m.drug_name.toLowerCase().includes('metformin'));

  if (['ibuprofen', 'naproxen', 'aspirin'].includes(otcDrug)) {
    response += `I would be careful with ${otcDrug}. `;

    if (takesLisinopril) {
      response += `You are taking Lisinopril for your blood pressure, and ${otcDrug} can make it less effective and put extra stress on your kidneys. `;
    }
    if (takesMetformin && hasKidneyConcern) {
      response += `With your diabetes and the medications you are on, NSAIDs like ${otcDrug} can affect kidney function. `;
    }

    response += `Acetaminophen, which is Tylenol, is generally a safer choice for pain relief with your medications. But please check with your doctor or pharmacist first, especially for regular use.`;
  } else if (otcDrug === 'acetaminophen') {
    const takesStatins = meds.some(m => m.drug_name.toLowerCase().includes('atorvastatin') || m.drug_name.toLowerCase().includes('simvastatin'));
    response += `Acetaminophen, which is Tylenol, is generally okay for occasional use with your current medications. `;
    if (takesStatins) {
      response += `Since you take Atorvastatin, avoid taking more than the recommended dose, as both can affect the liver. `;
    }
    response += `Do not take more than 3,000 milligrams in a day, and avoid it if you are drinking alcohol. If you need pain relief for more than a few days, talk to your doctor.`;
  } else if (otcDrug === 'tums') {
    const takesOmeprazole = meds.some(m => m.drug_name.toLowerCase().includes('omeprazole'));
    response += `Tums are generally safe for occasional heartburn. `;
    if (takesOmeprazole) {
      response += `Since you already take Omeprazole for acid reflux, you should not need Tums very often. If you are needing them frequently, let your doctor know because it might mean your Omeprazole dose needs adjusting. `;
    }
    response += `If you take other medications, take Tums at least 2 hours apart to avoid absorption issues.`;
  } else {
    response += `Before taking ${otcDrug}, please check with your pharmacist. They can make sure it does not interact with ${joinSpoken(meds.map(m => m.drug_name.split(/\s+/)[0]))} or any of your conditions.`;
  }

  return response;
}

function handleWarningSignsQuestion(): string {
  const emr = getEmrRecord();
  const warningSigns = emr?.dischargeSummary?.warningSignsToWatch ?? [];

  if (warningSigns.length === 0) {
    return `${greeting()}I do not have specific warning signs on file from your discharge, but in general, go to the emergency room if you have chest pain, difficulty breathing, sudden weakness or numbness on one side, blood sugar below 54, high fever, or any symptoms that feel like an emergency. When in doubt, call 911.`;
  }

  let response = `${greeting()}this is a really important question. Based on your discharge papers, here are the signs that mean you should call your doctor right away or go to the emergency room. `;

  const parts = warningSigns.map((sign, i) => `${i + 1}. ${sign}`);
  response += parts.join('. ');
  response += '. If any of these happen, do not wait. Call your doctor or go to the emergency room. If you have chest pain or sudden difficulty breathing, call 911 right away.';

  return response;
}

function handleStopMedication(question: string): string {
  const lower = question.toLowerCase();
  const meds = getPatientMedications();
  const diagnoses = getActiveDiagnoses();
  const med = extractDrugFromQuestion(question);

  // "Do I have to take these forever?"
  if (/\b(?:forever|always|rest\s+of\s+my\s+life)\b/.test(lower)) {
    const chronicConditions = diagnoses.filter(d => d.category === 'chronic').map(d => d.description.split(',')[0]);
    let response = `${greeting()}I understand that can feel discouraging. `;

    if (chronicConditions.length > 0) {
      response += `Some of your conditions, like ${joinSpoken(chronicConditions)}, are chronic, which means they are managed over the long term. The medications help keep these conditions under control and prevent them from getting worse. `;
    }

    response += `However, your doctors may adjust your doses or even reduce medications if your numbers improve. For example, if your HbA1c comes down with diet and exercise, your doctor may be able to lower your Metformin dose. `;
    response += `The best thing you can do is keep taking your medications as prescribed, follow your diet plan, stay active, and go to your follow-up appointments. Your endocrinologist can talk about long-term medication goals at your next visit.`;

    return response;
  }

  // "What happens if I stop taking X?"
  if (med) {
    const baseName = med.drug_name.split(/\s+/)[0].toLowerCase();
    const condition = DRUG_DIAGNOSIS_MAP[baseName];

    let response = `${greeting()}please do not stop taking ${med.drug_name} without talking to your doctor first. `;

    if (condition) {
      response += `You take it for your ${condition}. `;

      if (baseName === 'metformin') {
        response += 'If you stop Metformin, your blood sugar could go up, which over time can damage your nerves, kidneys, eyes, and heart. ';
      } else if (baseName === 'lisinopril') {
        response += 'If you stop Lisinopril, your blood pressure could rise, which increases your risk of heart attack and stroke. ';
      } else if (baseName === 'atorvastatin') {
        response += 'If you stop Atorvastatin, your cholesterol could go back up, increasing your risk of heart disease. Your LDL is already above the target at 138. ';
      } else if (baseName === 'omeprazole') {
        response += 'If you stop Omeprazole suddenly, you might get rebound acid reflux that is worse than before. If you want to stop, your doctor can help you taper off gradually. ';
      } else {
        response += `Stopping it could cause your ${condition} to get worse. `;
      }
    } else {
      response += 'Stopping it could affect your health. ';
    }

    response += 'If you are having side effects or concerns about the cost, talk to your doctor. They may be able to switch you to a different medication or adjust the dose.';
    return response;
  }

  // General "stop taking"
  return `${greeting()}I understand it can be hard to take medications every day. But each of your medications was prescribed to help manage a specific health condition. Stopping any of them without your doctor's guidance could cause your condition to get worse. If you are having side effects, trouble paying for medications, or just want to understand why you need them, please talk to your doctor. They can help.`;
}

function handleOverallStatus(): string {
  const emr = getEmrRecord();
  const diagnoses = getActiveDiagnoses();
  const labs = getAbnormalLabs();
  const vitals = getLatestVitals();
  const carePlan = emr?.carePlan;

  let response = `${greeting()}let me give you an overview of how things are looking. `;

  // Vitals trend
  if (vitals && vitals.systolicBP != null && vitals.diastolicBP != null) {
    response += `Your most recent blood pressure was ${vitals.systolicBP} over ${vitals.diastolicBP}, `;
    if (vitals.systolicBP < 130 && vitals.diastolicBP < 80) {
      response += 'which is right on target. ';
    } else if (vitals.systolicBP < 140) {
      response += 'which is close to the goal of under 130 over 80. ';
    } else {
      response += 'which is still a bit high. Keep taking your Lisinopril and watching your salt. ';
    }
  }

  // Lab highlights
  if (labs.length > 0) {
    const labParts: string[] = [];
    for (const lab of labs.slice(0, 3)) {
      const direction = lab.interpretation === 'high' || lab.interpretation === 'critical-high' ? 'a bit high' : 'a bit low';
      labParts.push(`your ${lab.testName} is ${direction} at ${lab.value} ${lab.unit}`);
    }
    response += `On your labs, ${joinSpoken(labParts)}. These are things your doctors are watching. `;
  }

  // Care plan goals
  if (carePlan?.goals && carePlan.goals.length > 0) {
    const activeGoals = carePlan.goals.filter(g => g.status === 'active');
    if (activeGoals.length > 0) {
      response += `Your care team has set ${activeGoals.length} health goals for you, including `;
      const goalNames = activeGoals.slice(0, 3).map(g => g.description.charAt(0).toLowerCase() + g.description.slice(1));
      response += `${joinSpoken(goalNames)}. `;
    }
  }

  // Positive framing
  if (emr?.dischargeSummary?.dischargeCondition) {
    response += `You were discharged in ${emr.dischargeSummary.dischargeCondition.toLowerCase().split('.')[0]} condition. `;
  }

  response += 'The most important things right now are to take your medications on time, follow your diet, walk a little each day, and go to your follow-up appointments. You are on the right track.';

  return response;
}

function handleHospitalizationReason(): string {
  const emr = getEmrRecord();
  if (!emr?.dischargeSummary) {
    return `${greeting()}I do not have your hospitalization details on file right now.`;
  }

  const ds = emr.dischargeSummary;
  const admDate = new Date(ds.admissionDate + 'T00:00:00');
  const disDate = new Date(ds.dischargeDate + 'T00:00:00');
  const admMonth = admDate.toLocaleString('en-US', { month: 'long' });
  const disMonth = disDate.toLocaleString('en-US', { month: 'long' });
  const stayDays = Math.round((disDate.getTime() - admDate.getTime()) / (1000 * 60 * 60 * 24));

  let response = `${greeting()}let me explain what happened. You were admitted to ${ds.hospitalName} on ${admMonth} ${admDate.getDate()} because of ${ds.admittingDiagnosis.toLowerCase()}. `;

  // Simplify the hospital course for a patient
  response += `You stayed for ${stayDays} days and were treated with antibiotics and your regular medications were managed. `;

  // Connect to their conditions
  const diagnoses = getActiveDiagnoses();
  const hasDiabetes = diagnoses.some(d => d.description.toLowerCase().includes('diabetes'));
  if (hasDiabetes && ds.hospitalCourse?.toLowerCase().includes('blood glucose')) {
    response += 'Your blood sugar was also high when you came in, which can happen when your body is fighting an infection. It came back under control with your Metformin. ';
  }

  response += `You were discharged on ${disMonth} ${disDate.getDate()} in ${ds.dischargeCondition.toLowerCase().split('.')[0]} condition. `;

  // What they need to do now
  if (ds.dischargeInstructions) {
    const keyInstructions = ds.dischargeInstructions.split('.').slice(0, 2).join('.') + '.';
    response += `The most important things your doctors want you to do now are: ${keyInstructions}`;
  }

  return response;
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

/**
 * Build a template-based response for common medication questions.
 *
 * Returns a spoken-friendly string for intents that can be answered from
 * structured data alone, or null for intents that require LLM synthesis
 * (complex drug_info, how_works, side_effect_concern, interactions analysis).
 *
 * @param intentType - The classified intent from the voice router
 * @param question   - The raw question text from the patient
 * @returns A TTS-ready response string, or null if the LLM is needed
 */
export function buildTemplateResponse(
  intentType: IntentType,
  question: string,
): string | null {
  switch (intentType) {
    // --- Schedule and dose tracking ---
    case 'next_dose':
      return handleNextDose();

    case 'show_schedule':
    case 'my_medications':
      return handleMyMedicationsList();

    // --- Dose logging ---
    case 'log_taken':
      return handleLogTaken(question);

    case 'log_skipped':
      return handleLogSkipped(question);

    // --- Help ---
    case 'help':
      return handleHelp();

    // --- EMR queries ---
    case 'show_emr':
    case 'emr_diagnosis':
      return handleEmrDiagnosis();

    case 'emr_allergies':
      return handleEmrAllergies();

    case 'lab_question':
      return handleLabQuestion();

    case 'emr_follow_up':
      return handleEmrFollowUp();

    case 'emr_discharge_info':
      return handleEmrDischargeInfo();

    // --- Drug data lookups ---
    case 'side_effects':
      return handleSideEffects(question);

    case 'missed_dose':
      return handleMissedDose(question);

    case 'food_interactions':
      return handleFoodInteractions(question);

    case 'why_taking':
      return handleWhyTaking(question);

    case 'storage':
      return handleStorage(question);

    case 'generic_vs_brand':
      return handleGenericVsBrand(question);

    // --- Cross-condition handlers (360-degree EMR view) ---
    case 'symptom_concern':
      return handleSymptomConcern(question);

    case 'emotional_concern':
      return handleEmotionalConcern(question);

    case 'diet_question':
      return handleDietQuestion(question);

    case 'otc_safe':
      return handleOtcSafety(question);

    case 'warning_signs':
      return handleWarningSignsQuestion();

    case 'stop_taking':
      return handleStopMedication(question);

    case 'overall_status':
      return handleOverallStatus();

    case 'hospitalization_reason':
      return handleHospitalizationReason();

    // --- Intents that need LLM synthesis ---
    // These require reasoning, not just data lookup:
    case 'drug_info':       // Complex drug explanations
    case 'interactions':    // Multi-drug interaction analysis
    case 'side_effect_concern': // "Should I worry about X?"
    case 'how_works':       // Mechanism of action explanation
    case 'overdose':        // Safety-critical — needs careful response
    case 'pregnancy':       // Safety-critical
    case 'alcohol':         // Drug-specific nuance
    case 'driving':         // Drug-specific nuance
    case 'cost':            // Needs current pricing data
    case 'pill_identify':   // Visual identification
    case 'add_medication':  // Workflow, not a question
    case 'unknown':         // Cannot template the unknown
    default:
      return null;
  }
}
